import { useNavigate } from 'react-router-dom';
import ProgressBar from '@/components/base/ProgressBar';
import StatusBadge from '@/components/base/StatusBadge';
import { formatCompact, formatMoney } from '@/utils/format';
import { toKesEquivalent } from '@/utils/invoice';
import type { CampaignReportRow } from '@/types/portal';

interface CampaignReportTableProps {
  rows: CampaignReportRow[];
}

export default function CampaignReportTable({ rows }: CampaignReportTableProps) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-14 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-xl text-foreground-500">
          <i className="ri-megaphone-line" />
        </span>
        <h3 className="mt-3 font-heading text-base font-semibold text-foreground-950">
          No campaigns match
        </h3>
        <p className="mt-1 max-w-sm text-sm text-foreground-600">
          Try another market or widen the reporting period. Reports update as campaigns progress.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-background-200 bg-background-50">
      <table className="row-accent w-full min-w-[1080px] border-collapse text-left">
        <thead>
          <tr className="border-b border-background-200 text-[11px] font-label font-semibold uppercase tracking-wider text-foreground-400">
            <th className="px-5 py-3 font-semibold">Campaign</th>
            <th className="px-3 py-3 font-semibold">Market</th>
            <th className="px-3 py-3 font-semibold">Budget</th>
            <th className="px-3 py-3 font-semibold">Spend vs budget</th>
            <th className="px-3 py-3 font-semibold">Creators</th>
            <th className="px-3 py-3 font-semibold">Reach</th>
            <th className="px-3 py-3 font-semibold">Engagement</th>
            <th className="px-5 py-3 text-right font-semibold">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const budgetKes = toKesEquivalent(row.budget, row.currency);
            const spendKes = toKesEquivalent(row.spend, row.currency);
            const pct = budgetKes > 0 ? Math.round((spendKes / budgetKes) * 100) : 0;
            return (
              <tr
                key={row.id}
                onClick={() => navigate(`/campaigns/${row.id}`)}
                className="cursor-pointer border-b border-background-100 last:border-0 transition-colors hover:bg-background-100"
              >
                <td className="px-5 py-3.5">
                  <p className="text-sm font-medium text-foreground-950">{row.name}</p>
                  <p className="text-xs text-foreground-500">
                    {row.brandClient} · {row.delivered}/{row.totalDeliverables} deliverables
                  </p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-xs text-foreground-700">{row.market}</p>
                  <p className="text-[11px] text-foreground-400">{row.markets.join(' · ')}</p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{formatMoney(row.budget, row.currency)}</p>
                  <p className="text-[11px] text-foreground-400">{formatMoney(budgetKes, 'KES')} equiv</p>
                </td>
                <td className="px-3 py-3.5">
                  <div className="w-40">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground-800">{formatMoney(row.spend, row.currency)}</span>
                      <span className={pct > 100 ? 'text-primary-700' : 'text-foreground-500'}>
                        {pct}%
                      </span>
                    </div>
                    <ProgressBar
                      value={pct}
                      tone={pct > 100 ? 'primary' : 'accent'}
                      className="mt-1.5"
                    />
                  </div>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{row.creators}</p>
                  <p className="text-[11px] text-foreground-400">contracted</p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm text-foreground-900">{formatCompact(row.reach)}</p>
                </td>
                <td className="px-3 py-3.5">
                  <p className="text-sm font-medium text-foreground-950">
                    {row.engagementRate.toFixed(1)}%
                  </p>
                  <p className="text-[11px] text-foreground-400">
                    {formatCompact(row.engagements)} engagements
                  </p>
                </td>
                <td className="px-5 py-3.5 text-right">
                  <StatusBadge status={row.status} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}